import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp, Eye, EyeOff, FlaskConical, Loader2, UserCog } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { FunctionalSelect, SelectItem } from './ui/Select';
import { getRoleCatalog } from '../services/rolesService';
import { api } from '../services/authService';
import { getCookie, setCookie, deleteCookie } from '../services/cookieService';
import { organizationsService, SELECTED_ORGANIZATION_EVENT } from '../services/organizationsService';
import { membershipsOf, firstAllowedPath, resolveSelectedOrgId } from '../services/permissions';

/** Same cookie the backend reads (ROLE_OVERRIDE_COOKIE); JSON {"roles": [...], "bulk_contributor": bool}. */
export const ROLE_OVERRIDE_COOKIE = 'cube_role_override';
/** Backend IMPERSONATE_COOKIE: the id of the user a platform admin is acting as. */
export const IMPERSONATE_COOKIE = 'cube_impersonate';
const ADMIN_USERS_LIST_COOKIE = 'admin_users_list';
const COLLAPSED_KEY = 'adminToolbarCollapsed';

function readOverride() {
  try {
    const raw = getCookie(ROLE_OVERRIDE_COOKIE);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed?.roles)) return null;
    return { roles: parsed.roles, bulk: Boolean(parsed.bulk_contributor) };
  } catch {
    return null;
  }
}

/** Drop every per-user cache so the next load reflects whoever we are now acting as. */
function clearActingUserCaches() {
  organizationsService.clearOrganizations();
  deleteCookie(ADMIN_USERS_LIST_COOKIE);
}

/**
 * Platform-admin test bar: switch the selected organization, impersonate another account, and
 * "view as" a role set (with or without the bulk-contributor flag). The choices go to the
 * backend as cookies, which swaps the acting user / drops the admin bypass for the session, so
 * pages, buttons and 403s behave exactly as they would for that person or role.
 */
function AdminToolbar() {
  const { user, reloadUser } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [draft, setDraft] = useState(() => readOverride()?.roles || []);
  // Stand-in for users.is_bulk_contributor while viewing as a role set.
  const [draftBulk, setDraftBulk] = useState(() => readOverride()?.bulk || false);
  const [applying, setApplying] = useState(false);
  const [orgId, setOrgId] = useState(() => organizationsService.getSelectedOrganization('id'));
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState('');
  // Hidden state is per browser; a small tab stays so the bar can always be brought back.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const setCollapsedPersist = (next) => {
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      /* per-browser convenience only */
    }
  };

  const active = Array.isArray(user?.role_override);
  const impersonating = user?.impersonating || null;
  const memberships = membershipsOf(user);
  const orgs = memberships.map((m) => ({ id: String(m.organization_id), name: m.organization_name }));
  const selectedOrgId = resolveSelectedOrgId(user, orgId);
  const canSwitch = Boolean(user?.can_switch_roles);
  // The users list is admin-only on the backend, so load it only while we really are the admin.
  const canListUsers = canSwitch && !impersonating && Boolean(user?.is_admin);

  useEffect(() => {
    if (!canSwitch) return undefined;
    getRoleCatalog().then(setCatalog).catch(() => setCatalog({ roles: [] }));
    const sync = () => setOrgId(organizationsService.getSelectedOrganization('id'));
    window.addEventListener(SELECTED_ORGANIZATION_EVENT, sync);
    return () => window.removeEventListener(SELECTED_ORGANIZATION_EVENT, sync);
  }, [canSwitch]);

  useEffect(() => {
    if (!canListUsers) return undefined;
    let cancelled = false;
    api
      .get('/api/v2/users/')
      .then((res) => {
        if (cancelled) return;
        const rows = (Array.isArray(res.data) ? res.data : []).filter((u) => u.is_active && String(u.id) !== String(user?.id));
        rows.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
        setUsers(rows);
      })
      .catch(() => !cancelled && setUsers([]));
    return () => {
      cancelled = true;
    };
  }, [canListUsers, user?.id]);

  if (!canSwitch) return null;

  const finishSwitch = async () => {
    const next = await reloadUser();
    // Land on a page the acting user / role can see.
    navigate(firstAllowedPath(next, resolveSelectedOrgId(next, organizationsService.getSelectedOrganization('id'))), {
      replace: true,
    });
  };

  const applyRoles = async (roles, bulk = false) => {
    setApplying(true);
    try {
      if (roles === null) deleteCookie(ROLE_OVERRIDE_COOKIE);
      else setCookie(ROLE_OVERRIDE_COOKIE, JSON.stringify({ roles, bulk_contributor: bulk }), 1);
      await finishSwitch();
    } finally {
      setApplying(false);
    }
  };

  const impersonate = async (targetId) => {
    setApplying(true);
    try {
      // Impersonation replaces any role override: we want exactly what that person sees.
      deleteCookie(ROLE_OVERRIDE_COOKIE);
      setDraft([]);
      setDraftBulk(false);
      if (targetId) setCookie(IMPERSONATE_COOKIE, String(targetId), 1);
      else deleteCookie(IMPERSONATE_COOKIE);
      clearActingUserCaches();
      await finishSwitch();
    } finally {
      setApplying(false);
    }
  };

  const toggleRole = (key) => {
    setDraft((prev) => (prev.includes(key) ? prev.filter((r) => r !== key) : [...prev, key]));
  };

  const handleOrgChange = (value) => {
    const parsed = organizationsService.parseOrganizationOptionValue(value);
    const nextId = parsed ? parsed.id : value;
    organizationsService.setSelectedOrganization(nextId, 'id', orgs);
  };

  const dirty =
    JSON.stringify(draft) !== JSON.stringify(user.role_override || []) ||
    draftBulk !== (active ? Boolean(user.is_bulk_contributor) : false);

  const chipClass = (on, tone = 'blue') =>
    `rounded-full border px-2 py-0.5 ${
      on
        ? tone === 'amber'
          ? 'border-amber-600 bg-amber-600 text-white'
          : 'border-[#0062FB] bg-[#0062FB] text-white'
        : 'border-hairline bg-surface text-zinc-600 hover:bg-[#E8EEF5]'
    }`;

  const q = userFilter.trim().toLowerCase();
  const visibleUsers = q
    ? users.filter((u) => [u.email, u.first_name, u.last_name].some((v) => (v || '').toLowerCase().includes(q)))
    : users;
  const userLabel = (u) => {
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
    return name ? `${name} · ${u.email}` : u.email;
  };

  const highlighted = active || Boolean(impersonating);
  let modeLabel = 'Admin test bar';
  if (impersonating && active) modeLabel = 'Impersonating + viewing as role';
  else if (impersonating) modeLabel = 'Impersonating';
  else if (active) modeLabel = 'Viewing as role';

  if (collapsed) {
    // Floating tab at the top-right of the content area: amber while something is overridden so
    // an impersonation or role view can never be forgotten behind a hidden bar.
    return (
      <div className="relative z-20 h-0">
        <button
          type="button"
          onClick={() => setCollapsedPersist(false)}
          title={highlighted ? `${modeLabel} — click to show the admin test bar` : 'Show the admin test bar'}
          className={`absolute right-3 top-0 inline-flex items-center gap-1 rounded-b-md border border-t-0 px-2 py-0.5 text-[11px] font-medium shadow-sm md:right-8 ${
            highlighted
              ? 'border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200'
              : 'border-hairline bg-[#F4F7FB] text-zinc-500 hover:bg-[#E8EEF5]'
          }`}
          data-testid="admin-toolbar-tab"
        >
          <FlaskConical className="h-3 w-3" />
          {highlighted ? modeLabel : 'Test bar'}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2 text-xs md:px-8 ${
        highlighted ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-hairline bg-[#F4F7FB] text-zinc-600'
      }`}
      data-testid="admin-toolbar"
    >
      <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide">
        {highlighted ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        {modeLabel}
      </span>

      {impersonating ? (
        <span className="inline-flex items-center gap-1.5">
          <UserCog className="h-3.5 w-3.5" />
          <span className="font-medium">{impersonating.name || impersonating.email}</span>
          <span className="opacity-70">({impersonating.email})</span>
          <button
            type="button"
            onClick={() => impersonate(null)}
            disabled={applying}
            className="ml-1 rounded-md border border-amber-400 px-2 py-0.5 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
          >
            Stop impersonating
          </button>
        </span>
      ) : canListUsers ? (
        <span className="inline-flex items-center gap-1">
          <input
            type="text"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            placeholder="Filter users…"
            aria-label="Filter users to impersonate"
            className="h-8 w-32 rounded-lg border border-hairline bg-surface px-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#18181B]/10"
          />
          <FunctionalSelect
            label="Impersonate"
            className="w-64"
            triggerClassName="h-8 text-xs"
            value=""
            onValueChange={(value) => impersonate(value)}
            placeholder={users.length === 0 ? 'Loading users…' : 'Pick a user'}
            disabled={applying || users.length === 0}
          >
            {visibleUsers.slice(0, 50).map((u) => (
              <SelectItem key={u.id} value={String(u.id)}>
                {userLabel(u)}
              </SelectItem>
            ))}
          </FunctionalSelect>
        </span>
      ) : null}

      <FunctionalSelect
        label="Org"
        className="w-56"
        triggerClassName="h-8 text-xs"
        value={organizationsService.getSelectedOptionValue(orgs, selectedOrgId, 'id')}
        onValueChange={handleOrgChange}
        placeholder="Select org"
        disabled={orgs.length === 0}
      >
        {orgs.map((o) => (
          <SelectItem key={o.id} value={organizationsService.orgToOptionValue(o)}>
            {o.name}
          </SelectItem>
        ))}
      </FunctionalSelect>

      <div className="flex flex-wrap items-center gap-1">
        {(catalog?.roles || []).map((role) => (
          <button
            key={role.key}
            type="button"
            onClick={() => toggleRole(role.key)}
            title={role.description}
            className={chipClass(draft.includes(role.key))}
          >
            {role.label}
          </button>
        ))}
        <span className="mx-1 h-4 border-l border-current opacity-30" aria-hidden="true" />
        <button
          type="button"
          onClick={() => setDraftBulk((v) => !v)}
          title="users.is_bulk_contributor: may submit bulk uploads (FastCube) and run FastID, on top of org roles"
          className={chipClass(draftBulk, 'amber')}
        >
          Bulk contributor flag
        </button>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <button
          type="button"
          onClick={() => applyRoles(draft, draftBulk)}
          disabled={applying || !dirty}
          className="inline-flex items-center gap-1 rounded-md bg-[#0062FB] px-3 py-1 font-medium text-white hover:bg-[#0052E0] disabled:opacity-50"
        >
          {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {draft.length === 0 && !draftBulk ? 'View with no roles' : 'View as'}
        </button>
        {active ? (
          <button
            type="button"
            onClick={() => {
              setDraft([]);
              setDraftBulk(false);
              applyRoles(null);
            }}
            disabled={applying}
            className="rounded-md border border-amber-400 px-3 py-1 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
          >
            {impersonating ? 'Back to their roles' : 'Back to admin'}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setCollapsedPersist(true)}
          title="Hide the admin test bar (a small tab stays to bring it back)"
          aria-label="Hide admin test bar"
          className="rounded-md p-1 hover:bg-black/5"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default AdminToolbar;
